/**
 * Entrega de documentos al cliente — sección "Entrega" del hub de Inventario
 * de Documentos (transferenciadocumentos.js), que ya pone la cabecera.
 *
 * La entrega es por LOTE: una persona se lleva los documentos de N chasis en
 * un solo acto, con un solo recibo. Antes era un chasis a la vez, así que
 * quien compraba cinco motores obligaba al cajero a repetir el flujo cinco
 * veces (cinco veces los datos de quien recibe, cinco recibos) y nada
 * relacionaba esas entregas entre sí. El orden de la pantalla es el del
 * mostrador: primero quién recibe, después qué se lleva.
 *
 * Contrato con el backend (entregadocumentosbackend.py):
 *   - /api/entrega_candidatos: los chasis con documentos recibidos en MI
 *     sucursal y todavía sin entregar —no la tabla `producto` completa, que
 *     es lo que bajaba /api/consultarmatricula para filtrar en el navegador—,
 *     con el estado de cada documento, el comprador (para agrupar por cédula)
 *     y los cobros de matrícula.
 *   - /api/entrega_procesar: el lote completo. TODO O NADA: si un chasis no
 *     pasa la validación no se entrega ninguno y el error dice cuál y por
 *     qué. El servidor revalida todo y devuelve el `lote` (uuid común a los
 *     N chasis) con el que se identifica la entrega después.
 *
 * Las reglas heredadas del escritorio se conservan y ahora las hace cumplir
 * el servidor: matrícula recibida en inventario, chasis con placa y endoso, y
 * documentos entregados en la sucursal donde están recibidos (comparación por
 * código de sucursal, nunca por label).
 *
 * La lógica del lote (armarlo, validarlo, resumirlo) vive en
 * lib/entregadocumentos, que es donde se prueba.
 */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, fpInputSx, EstadoVacio, ConfirmarDialog } from "components/financepro";
import ReporteRecepcionMatricula from "components/ReporteRecepcionMatricula";
import PDFModal from "components/PDFModal";
import { useApi, useSucursales, fechaDMY } from "lib/api";
import { labelTipo, codigoSucursal } from "lib/documentosvehiculo";
import {
  agregarChasis,
  agruparPorCliente,
  alternarTipo,
  chasisDeCliente,
  claveDocumento,
  filasRecibo,
  itemsLote,
  nombreCliente,
  quitarChasis,
  resumenLote,
  textoEstadoDocumento,
  totalesLote,
  validarLote,
} from "lib/entregadocumentos";

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};
// El tema (assets/theme/components/table/tableHead) pone display:block al
// TableHead y desalinea las columnas.
const tableHeadSx = { display: "table-header-group", p: 0, borderRadius: 0 };
const celda = { py: 0.8, px: 1, fontSize: "0.82rem", borderColor: FP.color.border };

function SectionTitle({ icon: Icon, children, color }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
SectionTitle.propTypes = {
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.string.isRequired,
};

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

const descripcionVehiculo = (c) =>
  [c.marca, c.modelo, c.colore, c.placa && `Placa ${c.placa}`].filter(Boolean).join(" · ");

const totalCobros = (cobros) => (cobros || []).reduce((a, p) => a + (parseFloat(p.valor) || 0), 0);

export default function EntregaMatriculas() {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [controller] = useMaterialUIController();
  const { usuarioLog } = controller;
  // `todas`: la sucursal de entrega puede no ser la del usuario logueado si
  // los documentos se recibieron en otra (el servidor valida cuál es).
  const sucursales = useSucursales({ todas: true });

  const [candidatos, setCandidatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [lote, setLote] = useState([]);

  const [nombres, setNombres] = useState("");
  const [apellidos, setApellidos] = useState("");
  const [cedula, setCedula] = useState("");
  const [fecha, setFecha] = useState(hoyISO());
  const [sucursalEntrega, setSucursalEntrega] = useState(null);
  const [miSucursal, setMiSucursal] = useState("");

  const [confirmar, setConfirmar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfAbierto, setPdfAbierto] = useState(false);
  const [pdfTitulo, setPdfTitulo] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/entrega_candidatos", {});
      if (!ok) {
        showAlert({ message: data.Error || "No se pudieron cargar los chasis", color: "error" });
        return;
      }
      setCandidatos(data.candidatos || []);
      setMiSucursal(data.misucursal || "");
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // Sucursal de entrega por defecto: la del usuario según el servidor (los
  // labels vienen como "2-Nombre", la comparación es por código).
  useEffect(() => {
    if (sucursalEntrega || !sucursales.length) return;
    const mia = miSucursal || localStorage.getItem("sucursal");
    setSucursalEntrega(
      sucursales.find((s) => codigoSucursal(s.label) === codigoSucursal(mia)) || null
    );
  }, [sucursales, miSucursal, sucursalEntrega]);

  const clientes = useMemo(() => agruparPorCliente(candidatos), [candidatos]);
  const disponibles = useMemo(
    () => candidatos.filter((c) => !lote.some((l) => l.chasis === c.chasis)),
    [candidatos, lote]
  );
  const totales = totalesLote(lote);
  const cobrosDelLote = useMemo(
    () => lote.flatMap((l) => (l.cobros || []).map((p) => ({ ...p, chasis: l.chasis }))),
    [lote]
  );

  const agregar = (candidato) => {
    if (!candidato) return;
    if (candidato.bloqueo) {
      showAlert({
        message: `${candidato.bloqueo} (chasis ${candidato.chasis})`,
        color: "error",
      });
      return;
    }
    setLote((prev) => agregarChasis(prev, candidato));
  };

  // Atajo del mostrador: el cliente llega por sus cinco motores, no por uno.
  const agregarLosDelCliente = (documento) => {
    const suyos = chasisDeCliente(candidatos, documento);
    const bloqueados = suyos.filter((c) => c.bloqueo);
    const libres = suyos.filter((c) => !c.bloqueo);
    setLote((prev) => libres.reduce((acc, c) => agregarChasis(acc, c), prev));
    if (bloqueados.length) {
      showAlert({
        message: `${bloqueados.length} chasis de este cliente no se pueden entregar: ${bloqueados
          .map((c) => `${c.chasis} (${c.bloqueo})`)
          .join("; ")}`,
        color: "warning",
      });
    }
  };

  const elegirCliente = (grupo) => {
    if (!grupo) return;
    setNombres(grupo.cliente.nombres || "");
    setApellidos(grupo.cliente.apellidos || "");
    setCedula(grupo.cliente.cedula || "");
    agregarLosDelCliente(grupo.cliente.cedula);
  };

  const limpiar = () => {
    setLote([]);
    setNombres("");
    setApellidos("");
    setCedula("");
    setFecha(hoyISO());
  };

  // Chasis del cliente que se está digitando y que todavía no están en el
  // lote: el recordatorio de que se puede entregar todo de una vez. Los
  // bloqueados no cuentan (el botón prometería agregar algo que no agrega);
  // se ven, con su motivo, en el buscador de chasis.
  const pendientesDelCliente = useMemo(() => {
    if (!claveDocumento(cedula)) return [];
    return chasisDeCliente(candidatos, cedula).filter(
      (c) => !c.bloqueo && !lote.some((l) => l.chasis === c.chasis)
    );
  }, [candidatos, cedula, lote]);

  const receptor = { nombres, apellidos, cedula };
  const error = validarLote({
    lote,
    receptor,
    fecha,
    sucursalEntrega: sucursalEntrega?.label || "",
  });

  const abrirConfirmacion = () => {
    if (error) {
      showAlert({ message: error, color: "warning" });
      return;
    }
    setConfirmar(true);
  };

  const generarRecibo = (loteId) => {
    const url = ReporteRecepcionMatricula.generarReciboEntrega({
      receptor,
      fecha: fechaDMY(fecha),
      sucursal: sucursalEntrega?.label || "",
      cajero: usuarioLog || "",
      lote: loteId,
      filas: filasRecibo(lote),
      pagos: cobrosDelLote.map((p) => ({
        nombres: p.cliente,
        apellidos: "",
        fecha: p.fecha,
        valor: fmtMoney(p.valor),
      })),
    });
    setPdfUrl(url);
    setPdfTitulo(`Recibo de entrega — ${resumenLote(lote)}`);
    setPdfAbierto(true);
  };

  const entregar = async () => {
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/entrega_procesar", {
        receptor: {
          nombres: nombres.trim(),
          apellidos: apellidos.trim(),
          cedula: cedula.trim(),
        },
        fecha,
        sucursalentrega: sucursalEntrega.label,
        items: itemsLote(lote),
      });
      if (!ok) {
        // Todo o nada: si el servidor rechaza el lote no se entregó nada, así
        // que se deja armado para corregir el chasis que nombra el error.
        showAlert({ message: data.Error || "No se pudo registrar la entrega", color: "error" });
        return;
      }
      showAlert({ message: data.mensajeok, color: "success" });
      setConfirmar(false);
      generarRecibo(data.lote);
      limpiar();
      cargar();
    } finally {
      setGuardando(false);
    }
  };

  const cerrarPdf = () => {
    setPdfAbierto(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  return (
    <Box>
      {/* Quién recibe: primero la persona, después lo que se lleva. */}
      <Box sx={sectionBox}>
        <SectionTitle icon={PersonOutlineOutlinedIcon} color={FP.color.navy}>
          Quién recibe
        </SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              options={clientes}
              value={null}
              blurOnSelect
              loading={cargando}
              onChange={(_, v) => elegirCliente(v)}
              getOptionLabel={(o) => nombreCliente(o.cliente) || o.cliente.cedula || ""}
              isOptionEqualToValue={(o, v) => o.clave === v.clave}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase();
                return opts
                  .filter((o) =>
                    `${nombreCliente(o.cliente)} ${o.cliente.cedula}`.toLowerCase().includes(q)
                  )
                  .slice(0, 60);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.clave}>
                  <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                      {nombreCliente(o.cliente) || "Sin nombre"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                      {o.cliente.cedula} — {o.chasis.length} chasis con documentos pendientes
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Buscar cliente (nombre o cédula)"
                  helperText="Trae sus datos y agrega todos sus chasis al lote"
                  sx={fpInputSx}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              required
              label="Nombres"
              value={nombres}
              onChange={(e) => setNombres(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              label="Apellidos"
              value={apellidos}
              onChange={(e) => setApellidos(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              label="Cédula"
              value={cedula}
              onChange={(e) => setCedula(e.target.value)}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              required
              type="date"
              label="Fecha de entrega"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fpInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={sucursales}
              value={sucursalEntrega}
              getOptionLabel={(o) => o?.label || ""}
              isOptionEqualToValue={(o, v) => o.label === v.label}
              onChange={(_, v) => setSucursalEntrega(v)}
              renderInput={(p) => (
                <TextField {...p} required label="Sucursal de entrega" sx={fpInputSx} />
              )}
            />
          </Grid>
        </Grid>
        {pendientesDelCliente.length > 0 && (
          <Box sx={{ mt: 1.6 }}>
            <Button
              size="small"
              startIcon={<GroupAddOutlinedIcon />}
              onClick={() => agregarLosDelCliente(cedula)}
              sx={btnOutline(FP.color.accentBlue)}
            >
              Agregar los otros {pendientesDelCliente.length} chasis de esta cédula
            </Button>
          </Box>
        )}
      </Box>

      {/* Chasis y documentos del lote */}
      <Box sx={{ ...sectionBox, mb: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.success}>
            Documentos a entregar ({resumenLote(lote)})
          </SectionTitle>
          <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Tooltip title="Recargar los chasis pendientes">
              <span>
                <IconButton onClick={cargar} disabled={cargando} size="small">
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button onClick={limpiar} disabled={guardando} sx={btnOutline(FP.color.navy)}>
              Limpiar
            </Button>
            <Button
              startIcon={
                guardando ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AssignmentTurnedInOutlinedIcon />
                )
              }
              disabled={guardando || totales.documentos === 0}
              onClick={abrirConfirmacion}
              sx={btnSolid(FP.color.success, "#2E7D32")}
            >
              {totales.documentos ? `Entregar ${resumenLote(lote)}` : "Entregar"}
            </Button>
          </Stack>
        </Box>

        <Autocomplete
          options={disponibles}
          loading={cargando}
          value={null}
          blurOnSelect
          onChange={(_, v) => agregar(v)}
          getOptionLabel={(o) => o.chasis || ""}
          filterOptions={(opts, { inputValue }) => {
            const q = inputValue.toLowerCase();
            return opts
              .filter((o) =>
                `${o.chasis} ${o.marca} ${o.modelo} ${o.placa} ${nombreCliente(o.cliente)} ${
                  o.cliente?.cedula || ""
                }`
                  .toLowerCase()
                  .includes(q)
              )
              .slice(0, 60);
          }}
          renderOption={(props, o) => (
            <li {...props} key={o.chasis}>
              <Box sx={{ width: "100%" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", fontFamily: "monospace" }}>
                  {o.chasis}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                  {[descripcionVehiculo(o), nombreCliente(o.cliente)].filter(Boolean).join(" · ")} —{" "}
                  {o.entregables.map(labelTipo).join(", ")}
                  {o.bloqueo ? ` · ${o.bloqueo}` : ""}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(p) => (
            <TextField
              {...p}
              label="Agregar chasis (chasis, placa, marca o cliente)"
              helperText={
                cargando
                  ? "Cargando…"
                  : `${disponibles.length} chasis con documentos por entregar en tu sucursal`
              }
              sx={{ ...fpInputSx, mb: 2 }}
            />
          )}
        />

        {lote.length === 0 ? (
          <EstadoVacio
            icono={cargando ? DirectionsCarFilledOutlinedIcon : SearchOffOutlinedIcon}
            titulo={cargando ? "Cargando chasis…" : "El lote está vacío"}
            subtitulo={
              cargando
                ? ""
                : "Busque al cliente para traer todos sus chasis de una vez, o agréguelos uno a uno."
            }
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 760 }}>
              <TableHead sx={tableHeadSx}>
                <TableRow>
                  <TableCell sx={{ ...celda, fontWeight: 800 }}>Chasis</TableCell>
                  <TableCell sx={{ ...celda, fontWeight: 800 }}>Vehículo</TableCell>
                  <TableCell sx={{ ...celda, fontWeight: 800 }}>
                    Documentos (clic para quitar/poner)
                  </TableCell>
                  <TableCell sx={{ ...celda, width: 60 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lote.map((l) => {
                  const noOfrecidos = (l.documentos || []).filter(
                    (d) => !l.entregables.includes(d.tipo)
                  );
                  const cobros = totalCobros(l.cobros);
                  return (
                    <TableRow key={l.chasis}>
                      <TableCell sx={{ ...celda, fontFamily: "monospace", fontWeight: 700 }}>
                        {l.chasis}
                        {cobros > 0 && (
                          <Typography sx={{ fontSize: "0.7rem", color: FP.color.textMuted }}>
                            Matrícula pagada RD$ {fmtMoney(cobros)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={celda}>
                        {descripcionVehiculo(l)}
                        {l.consignado && (
                          <Typography sx={{ fontSize: "0.7rem", color: FP.color.textMuted }}>
                            Consignado: {l.consignado}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={celda}>
                        <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                          {l.entregables.map((t) => {
                            const on = l.tipos.includes(t);
                            return (
                              <Chip
                                key={t}
                                size="small"
                                label={labelTipo(t)}
                                icon={on ? <TaskAltOutlinedIcon /> : undefined}
                                onClick={() => setLote((prev) => alternarTipo(prev, l.chasis, t))}
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: on ? "#E8F5E9" : FP.color.surfaceAlt,
                                  color: on ? "#2E7D32" : FP.color.textMuted,
                                  textDecoration: on ? "none" : "line-through",
                                  "& .MuiChip-icon": { color: "#2E7D32" },
                                }}
                              />
                            );
                          })}
                          {/* Los que no se ofrecen, con el porqué: no recibidos,
                              ya entregados o en otra sucursal. */}
                          {noOfrecidos.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: "0.72rem",
                                color: FP.color.textMuted,
                                alignSelf: "center",
                              }}
                            >
                              (
                              {noOfrecidos
                                .map((d) => `${labelTipo(d.tipo)}: ${textoEstadoDocumento(d)}`)
                                .join(" · ")}
                              )
                            </Typography>
                          )}
                          {l.entregables.some((t) =>
                            (l.documentos || []).some((d) => d.tipo === t && d.entransito)
                          ) && (
                            <Typography
                              sx={{ fontSize: "0.72rem", color: "#E65100", alignSelf: "center" }}
                            >
                              Hay un envío en tránsito con documentos de este chasis: confirme que
                              están en el mostrador.
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={celda} align="right">
                        <Tooltip title="Quitar del lote">
                          <IconButton
                            size="small"
                            onClick={() => setLote((prev) => quitarChasis(prev, l.chasis))}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {lote.length > 0 && error && (
          <Typography sx={{ fontSize: "0.82rem", color: "#C62828", fontWeight: 700, mt: 1.4 }}>
            {error}
          </Typography>
        )}

        {/* Sin lote todavía: a quién se le puede entregar hoy en esta sucursal. */}
        {!cargando && lote.length === 0 && clientes.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.74rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: FP.color.textSecondary,
                mb: 1,
              }}
            >
              Clientes con documentos pendientes en esta sucursal ({clientes.length})
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {clientes.slice(0, 12).map((g) => (
                <Chip
                  key={g.clave}
                  label={`${nombreCliente(g.cliente) || g.cliente.cedula} (${g.chasis.length})`}
                  onClick={() => elegirCliente(g)}
                  sx={{
                    fontWeight: 700,
                    bgcolor: FP.color.surfaceAlt,
                    color: FP.color.textPrimary,
                    "&:hover": { bgcolor: FP.color.accentBlueSoft },
                  }}
                />
              ))}
              {clientes.length > 12 && (
                <Typography
                  sx={{ fontSize: "0.8rem", color: FP.color.textSecondary, alignSelf: "center" }}
                >
                  y {clientes.length - 12} más — búsquelos arriba
                </Typography>
              )}
            </Stack>
          </Box>
        )}
      </Box>

      <ConfirmarDialog
        abierto={confirmar}
        titulo="Confirmar entrega"
        mensaje={`Se entregarán ${resumenLote(
          lote
        )} a ${nombres.trim()} ${apellidos.trim()} (${cedula.trim()}) en ${
          sucursalEntrega?.label || ""
        }: ${filasRecibo(lote)
          .map((f) => `${f.chasis} (${f.documentos})`)
          .join(
            "; "
          )}. Es una sola operación: si alguno no se puede entregar, no se entrega ninguno.`}
        textoConfirmar="Entregar"
        cargando={guardando}
        onCerrar={() => setConfirmar(false)}
        onConfirmar={entregar}
      />

      <PDFModal
        open={pdfAbierto}
        onClose={cerrarPdf}
        pdfUrl={pdfUrl}
        title={pdfTitulo}
        showDownloadButton
        showPrintButton
      />
    </Box>
  );
}
